/**
 * Single document (image or PDF) uploader backed by object storage (via the
 * API) — the single-file counterpart to MultiImageUploader.tsx. Value is one
 * public URL string; wire it straight into a Form.Item.
 *
 * Usage (inside an antd Form):
 *   <Form.Item name="tdsCertUrl"><DocumentUploader folder="payments" /></Form.Item>
 */
import { useState } from 'react'
import { Upload, Button, message } from 'antd'
import { UploadOutlined, LoadingOutlined, FileDoneOutlined, DeleteOutlined } from '@ant-design/icons'
import type { UploadFolder } from '@/services/api'
import { api, ApiError } from '@/services/api'

interface Props {
  folder: UploadFolder
  value?: string
  onChange?: (url: string | undefined) => void
  accept?: string
}

export default function DocumentUploader({ folder, value, onChange, accept = 'image/*,.pdf' }: Props) {
  const [loading, setLoading] = useState(false)

  const handleUpload = async (file: File) => {
    setLoading(true)
    try {
      const { url } = await api.uploadImage(folder, file)
      onChange?.(url)
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : 'Upload failed')
    } finally {
      setLoading(false)
    }
    return false // prevent antd's own request — api.uploadImage already sent it
  }

  const handleRemove = () => {
    if (value) void api.deleteImage(value).catch(() => {})
    onChange?.(undefined)
  }

  if (value) {
    const filename = decodeURIComponent(value.split('/').pop() || 'Uploaded file')
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#059669' }}
        >
          <FileDoneOutlined /> {filename}
        </a>
        <Button size="small" danger type="text" icon={<DeleteOutlined />} onClick={handleRemove} />
      </div>
    )
  }

  return (
    <Upload accept={accept} showUploadList={false} beforeUpload={(file) => handleUpload(file as unknown as File)}>
      <Button icon={loading ? <LoadingOutlined /> : <UploadOutlined />} loading={loading}>
        {loading ? 'Uploading…' : 'Upload file'}
      </Button>
    </Upload>
  )
}
